import React, { useState, useCallback, useEffect } from 'react';
import { Copy, Check, LinkedinLogo, Trash, PaperPlaneTilt, SpinnerGap, CheckCircle, XCircle, Clock, User, DownloadSimple } from '@phosphor-icons/react';
import {
    AlertDialog,
    AlertDialogTrigger,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogFooter,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogAction,
    AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { ContractText } from '@/components/system/ContractUI';
import { removeScoutCandidate, executeScoutBatch, exportScoutBatch } from '@/lib/api';
import { AGENT_CONFIG, WORK_TYPES } from '@/config/task-display-config';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

// ── Config-driven workflow labels (fallback to frontend config if API unreachable) ──
const SCOUT_CONFIG = AGENT_CONFIG.lead_gen_scout;
const FALLBACK_LABELS = SCOUT_CONFIG.workflowModes.manual;

/**
 * ScoutTaskDetail — LinkedIn Scout Candidate Batch Renderer (V2.1)
 * Side panel (left) + candidate list (right). Checkbox bulk selection.
 * Per-candidate personalized connection messages with A/B variant tracking.
 */

export function ScoutTaskDetail({ item, onAccept }) {

    const [checkedIds, setCheckedIds] = useState(new Set());
    const [selectedId, setSelectedId] = useState(null);
    const [removingIds, setRemovingIds] = useState(new Set());
    const [removedIds, setRemovedIds] = useState(new Set());
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [progress, setProgress] = useState(null);
    const [copied, setCopied] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState(null);
    const [manualWorkflowEnabled, setManualWorkflowEnabled] = useState(true);
    const [workflowLabels, setWorkflowLabels] = useState(FALLBACK_LABELS);

    const candidates = item.payload?.candidates || [];
    const connectionMessage = item.payload?.connection_message || '';
    const fundingYear = item.payload?.funding_year;

    const visible = candidates.filter(c => !removedIds.has(String(c._id)));
    const sendable = visible.filter(c => !checkedIds.has(String(c._id)));
    const sendCount = sendable.length;
    const selectedCandidate = selectedId ? visible.find(c => String(c._id) === selectedId) : null;

    // ── Checkbox handlers ──────────────────────────────────────────
    const toggleCheck = useCallback((id) => {
        setCheckedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    // ── Select candidate for detail panel ───────────────────────────
    const selectCandidate = useCallback((id) => {
        setSelectedId(prev => prev === id ? null : id);
    }, []);

    // ── Single remove ──────────────────────────────────────────────
    const handleRemove = useCallback(async (candidateId) => {
        const id = String(candidateId);
        setRemovingIds(prev => new Set([...prev, id]));
        try {
            const result = await removeScoutCandidate((item.id || '').replace(/^scout_batch:/, ''), id);
            if (result.status === 'ok') {
                setRemovedIds(prev => new Set([...prev, id]));
                setCheckedIds(prev => {
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
                if (selectedId === id) setSelectedId(null);
            }
        } catch (err) {
            console.error('[ScoutTaskDetail] Remove failed', err);
            toast.error('Could not remove candidate — please try again');
        } finally {
            setRemovingIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    }, [item.id, selectedId]);

    // ── Bulk remove ────────────────────────────────────────────────
    const handleBulkRemove = useCallback(async () => {
        const ids = [...checkedIds];
        for (const id of ids) {
            setRemovingIds(prev => new Set([...prev, id]));
            try {
                const result = await removeScoutCandidate((item.id || '').replace(/^scout_batch:/, ''), id);
                if (result.status === 'ok') {
                    setRemovedIds(prev => new Set([...prev, id]));
                    if (selectedId === id) setSelectedId(null);
                }
            } catch (err) {
                console.error('[ScoutTaskDetail] Bulk remove failed for', id, err);
                toast.error('Could not remove candidate — please try again');
            } finally {
                setRemovingIds(prev => {
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
            }
        }
        setCheckedIds(new Set());
    }, [checkedIds, item.id, selectedId]);

    // ── Copy connection message (per-candidate personalized) ────────
    const handleCopy = useCallback(async (candidateId, candidateMessage) => {
        const textToCopy = candidateMessage || connectionMessage;
        try {
            await navigator.clipboard.writeText(textToCopy);
        } catch {
            const ta = document.createElement('textarea');
            ta.value = textToCopy;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
        }
        setCopied(candidateId);
        setTimeout(() => setCopied(null), 2000);
    }, [connectionMessage]);

    // ── Check manual workflow config on mount ─────────────────────
    useEffect(() => {
        let cancelled = false;
        exportScoutBatch(item.id)
            .then((data) => {
                if (!cancelled) {
                    setManualWorkflowEnabled(!!data?.config?.manual_workflow_enabled);
                    // Config-driven labels from backend — single source of truth
                    const mode = data?.config?.workflow_mode || 'manual';
                    const serverLabels = data?.config?.workflow_labels;
                    const localLabels = SCOUT_CONFIG.workflowModes[mode];
                    setWorkflowLabels(serverLabels || localLabels || FALLBACK_LABELS);
                }
            })
            .catch(() => {
                // Fail-open: show manual workflow tools if config check fails
            });
        return () => { cancelled = true; };
    }, [item.id]);

    // ── Download list as XLSX ─────────────────────────────────────
    const handleDownloadList = useCallback(async () => {
        setDownloading(true);
        setDownloadError(null);
        try {
            const data = await exportScoutBatch(item.id);

            if (!data.rows || data.rows.length === 0) {
                setDownloadError('No candidates to export');
                return;
            }

            const excelRows = data.rows.map(r => ({
                'Name': r.name,
                'Startup': r.startup,
                'LinkedIn URL': r.linkedin_url,
                'Message': r.message,
                'Variant': r.variant,
                'Funding': r.funding || 'N/A',
                'Status': r.status,
            }));

            const ws = XLSX.utils.json_to_sheet(excelRows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Scout Batch');

            ws['!cols'] = [
                { wch: 20 },
                { wch: 25 },
                { wch: 50 },
                { wch: 60 },
                { wch: 8 },
                { wch: 15 },
                { wch: 12 },
            ];

            const today = new Date().toISOString().split('T')[0];
            const fileName = `scout_batch_${today}_${data.candidates_count}candidates.xlsx`;
            XLSX.writeFile(wb, fileName);
        } catch (err) {
            setDownloadError('Could not download list');
            console.error('[ScoutTaskDetail] Download failed', err);
        } finally {
            setDownloading(false);
        }
    }, [item.id]);

    // ── Execute batch (manual mode: approve + download, automated: send via extension) ──
    const handleExecute = useCallback(async () => {
        setSending(true);
        setConfirmOpen(false);

        if (manualWorkflowEnabled) {
            // Manual mode: approve the batch and trigger Excel download
            try {
                await handleDownloadList();
                toast.success(workflowLabels.toast_success || 'Batch approved');
                setTimeout(() => {
                    if (onAccept) onAccept(item.id, WORK_TYPES.LEAD_GEN_SCOUT);
                }, 1500);
            } catch (err) {
                console.error('[ScoutTaskDetail] Manual approve failed', err);
                toast.error('Could not approve batch');
                setSending(false);
            }
            return;
        }

        // Automated mode: approve-then-execute (dormant — SCOUT_MANUAL_WORKFLOW_ENABLED
        // stays True; scout stays OFF per the build freeze). The backend send gate
        // (work_inbox.py approve_work_item, LEAD_GEN_SCOUT branch) mints the CEO
        // send-authorization stamp on approve; POST /lead-gen/scout/execute
        // (lead_gen.py execute_scout_batch) verifies that stamp before queuing any
        // candidate — fail-closed with scout_send_blocked_unauthenticated otherwise.
        // Approve MUST resolve before execute is called (constants.py
        // SCOUT_MANUAL_WORKFLOW_ENABLED warning, WI-FREEZE-EXIT-SHORTLIST-REPAIRS-002
        // Leg 1 / WI-INFRA-UPGRADE-CLOSEOUT-BUNDLE-001 Leg E).
        const initialProgress = new Map();
        sendable.forEach(c => initialProgress.set(String(c._id), 'pending'));

        try {
            if (onAccept) await onAccept(item.id, WORK_TYPES.LEAD_GEN_SCOUT);

            setProgress(initialProgress);
            setTimeout(() => {
                setProgress(prev => {
                    const next = new Map(prev);
                    next.forEach((_, key) => next.set(key, 'sending'));
                    return next;
                });
            }, 300);

            const result = await executeScoutBatch((item.id || '').replace(/^scout_batch:/, ''));

            if (result.outcome === 'scout_batch_executed') {
                setProgress(prev => {
                    const next = new Map(prev);
                    next.forEach((_, key) => next.set(key, 'sent'));
                    return next;
                });
                toast.success(workflowLabels.toast_success || result.message);
            } else {
                toast.warning(result.message || 'Unexpected response');
                setProgress(null);
                setSending(false);
            }
        } catch (err) {
            console.error('[ScoutTaskDetail] Execute failed', err);
            toast.error('Could not queue connection requests');
            setProgress(null);
            setSending(false);
        }
    }, [item.id, sendable, onAccept, manualWorkflowEnabled, handleDownloadList, workflowLabels]);

    return (
        <div className="space-y-5">
            {/* Header Metadata */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-100">
                <div>
                    <ContractText variant="sectionLabel">Scout Batch</ContractText>
                    {fundingYear && (
                        <ContractText variant="caption" className="text-slate-500 mt-1">
                            Cohort: <span className="font-mono text-slate-700">{fundingYear}</span>
                        </ContractText>
                    )}
                </div>
                <div className="flex items-center gap-4">
                    {/* Download List — Utility action (DESIGN_LANGUAGE_SKILL Section 4: never in action bar) */}
                    {manualWorkflowEnabled && visible.length > 0 && !progress && (
                        <div className="space-y-1">
                            <Button
                                variant="outline"
                                size="sm"
                                className="gap-1.5 font-heading text-slate-600 border-slate-200 hover:border-brand-900 hover:text-brand-900 text-sm font-semibold"
                                onClick={handleDownloadList}
                                disabled={downloading}
                                aria-label={`Download list of ${visible.length} candidates as Excel file`}
                            >
                                {downloading
                                    ? <><SpinnerGap weight="bold" className="w-4 h-4 animate-spin" /> Preparing...</>
                                    : <><DownloadSimple weight="bold" className="w-4 h-4" /> Download List</>
                                }
                            </Button>
                            {downloadError && (
                                <p className="text-xs font-medium text-red-600">{downloadError}</p>
                            )}
                        </div>
                    )}
                    <div className="text-right">
                        <ContractText variant="stat">{visible.length}</ContractText>
                        <ContractText variant="caption" className="tracking-wide">Candidates</ContractText>
                    </div>
                </div>
            </div>

            {/* Side Panel + Candidate List Layout */}
            {visible.length > 0 && (
                <div className="flex gap-4">
                    {/* Detail Panel (left, ~40%) */}
                    <div className="w-2/5 shrink-0">
                        {selectedCandidate ? (
                            <div className="border border-slate-200 rounded-lg p-4 space-y-4 bg-white sticky top-4">
                                {/* Founder heading */}
                                <div>
                                    <h3 className="text-xl font-bold text-slate-900">
                                        {selectedCandidate.founder_name || 'Unknown'}
                                    </h3>
                                    <p className="text-base text-slate-700 mt-1">
                                        {selectedCandidate.company_name}
                                    </p>
                                </div>

                                {/* Meta details */}
                                <div className="space-y-1.5">
                                    {selectedCandidate.founder_title && (
                                        <p className="text-base text-slate-600">{selectedCandidate.founder_title}</p>
                                    )}
                                    {selectedCandidate.sector && (
                                        <p className="text-base text-slate-600">
                                            Sector: <span className="font-medium text-slate-800">{selectedCandidate.sector}</span>
                                        </p>
                                    )}
                                    {(selectedCandidate.total_funding_usd || selectedCandidate.funding_amount) && (
                                        <p className="text-base text-slate-600">
                                            Funding: <span className="font-medium text-slate-800">
                                                ${typeof (selectedCandidate.total_funding_usd || selectedCandidate.funding_amount) === 'number'
                                                    ? (selectedCandidate.total_funding_usd || selectedCandidate.funding_amount).toLocaleString()
                                                    : (selectedCandidate.total_funding_usd || selectedCandidate.funding_amount)}
                                            </span>
                                        </p>
                                    )}
                                    {selectedCandidate.funding_year && (
                                        <p className="text-base text-slate-600">
                                            Year: <span className="font-medium text-slate-800">{selectedCandidate.funding_year}</span>
                                        </p>
                                    )}
                                </div>

                                {/* LinkedIn Profile button */}
                                {selectedCandidate.founder_linkedin_url && (
                                    <a
                                        href={selectedCandidate.founder_linkedin_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-4 py-2.5 text-base font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors min-h-[44px] w-full justify-center"
                                        aria-label={`View ${selectedCandidate.founder_name || 'founder'} on LinkedIn`}
                                    >
                                        <LinkedinLogo weight="bold" className="w-5 h-5" />
                                        LinkedIn Profile
                                    </a>
                                )}

                                {/* Connection message box (per-candidate personalized) */}
                                {(selectedCandidate.connection_message || connectionMessage) && (
                                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                                        <p className="text-sm font-semibold text-slate-500 mb-1.5">
                                            Connection Message
                                            {selectedCandidate.message_variant && (
                                                <span className="ml-1.5 text-xs font-mono bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">
                                                    {selectedCandidate.message_variant}
                                                </span>
                                            )}
                                        </p>
                                        <p className="text-base text-slate-700 italic leading-relaxed mb-3">
                                            &ldquo;{selectedCandidate.connection_message || connectionMessage}&rdquo;
                                        </p>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleCopy(String(selectedCandidate._id), selectedCandidate.connection_message);
                                            }}
                                            className="inline-flex items-center gap-1.5 px-3 py-2 text-base font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:border-brand-900 hover:text-brand-900 transition-colors min-h-[44px]"
                                        >
                                            {copied === String(selectedCandidate._id)
                                                ? <><Check weight="bold" className="w-4 h-4 text-emerald-600" /> Copied</>
                                                : <><Copy weight="bold" className="w-4 h-4" /> Copy Message</>
                                            }
                                        </button>
                                    </div>
                                )}

                                {/* Remove button */}
                                {!progress && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleRemove(selectedCandidate._id); }}
                                        disabled={removingIds.has(String(selectedCandidate._id))}
                                        className="inline-flex items-center gap-1.5 px-3 py-2 text-base font-semibold text-red-600 bg-white border border-slate-200 rounded-lg hover:border-brand-900 transition-colors min-h-[44px] w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                        aria-label={`Remove ${selectedCandidate.founder_name || 'candidate'} from batch`}
                                    >
                                        <Trash weight="bold" className="w-4 h-4" />
                                        {removingIds.has(String(selectedCandidate._id)) ? 'Removing...' : 'Remove from Batch'}
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="border border-slate-200 rounded-lg p-6 bg-slate-50 flex flex-col items-center justify-center text-center min-h-[200px]">
                                <User weight="light" className="w-10 h-10 text-slate-300 mb-3" />
                                <p className="text-base font-medium text-slate-500">Click a candidate to view details</p>
                            </div>
                        )}
                    </div>

                    {/* Candidate List (right, ~60%) */}
                    <div className="flex-1 min-w-0">
                        <div className="border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100">
                            {visible.map((c) => {
                                const id = String(c._id);
                                const isSelected = selectedId === id;
                                const isChecked = checkedIds.has(id);
                                const isRemoving = removingIds.has(id);
                                const progressStatus = progress?.get(id);

                                return (
                                    <div
                                        key={id}
                                        className={`flex items-center gap-3 px-4 py-4 cursor-pointer transition-colors ${
                                            isSelected
                                                ? 'bg-blue-50 border-l-4 border-l-blue-500'
                                                : 'hover:bg-slate-50 border-l-4 border-l-transparent'
                                        }`}
                                        onClick={() => selectCandidate(id)}
                                        role="button"
                                        aria-pressed={isSelected}
                                    >
                                        {/* Checkbox — 44px tap target, native HTML for reliability */}
                                        {!progress && (
                                            <div
                                                className="w-11 h-11 flex items-center justify-center shrink-0"
                                                onClick={(e) => { e.stopPropagation(); toggleCheck(id); }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={() => toggleCheck(id)}
                                                    className="w-5 h-5 accent-emerald-600 cursor-pointer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    aria-label={`Select ${c.founder_name || 'candidate'}`}
                                                />
                                            </div>
                                        )}

                                        {/* Progress icon */}
                                        {progressStatus && (
                                            <div className="w-11 h-11 flex items-center justify-center shrink-0">
                                                {progressStatus === 'pending' && <Clock weight="bold" className="w-5 h-5 text-slate-400" />}
                                                {progressStatus === 'sending' && <SpinnerGap weight="bold" className="w-5 h-5 text-blue-500 animate-spin" />}
                                                {progressStatus === 'sent' && <CheckCircle weight="fill" className="w-5 h-5 text-emerald-500" />}
                                                {progressStatus === 'failed' && <XCircle weight="fill" className="w-5 h-5 text-red-500" />}
                                            </div>
                                        )}

                                        {/* Candidate info — simplified: name + startup only */}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-base font-bold text-slate-900 truncate">
                                                {c.founder_name || 'Unknown'}
                                                <span className="font-normal text-slate-600"> &mdash; {c.company_name}</span>
                                            </p>
                                        </div>

                                        {/* Progress label */}
                                        {progressStatus && (
                                            <span className={`text-sm font-medium shrink-0 ${
                                                progressStatus === 'sent' ? 'text-emerald-600' :
                                                progressStatus === 'failed' ? 'text-red-600' :
                                                progressStatus === 'sending' ? 'text-blue-600' :
                                                'text-slate-400'
                                            }`}>
                                                {progressStatus === 'pending' && 'Waiting'}
                                                {progressStatus === 'sending' && 'Sending...'}
                                                {progressStatus === 'sent' && 'Sent'}
                                                {progressStatus === 'failed' && 'Could not send'}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Empty state */}
            {visible.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                    <p className="text-base font-medium">All candidates have been removed</p>
                </div>
            )}

            {/* Update List (bulk remove) — above Send button */}
            {checkedIds.size > 0 && !progress && (
                <Button
                    variant="outline"
                    className="w-full min-h-[48px] gap-2 text-red-600 border-slate-200 hover:border-brand-900 text-base font-semibold"
                    onClick={handleBulkRemove}
                >
                    <Trash weight="bold" className="w-5 h-5" />
                    Remove Selected ({checkedIds.size})
                </Button>
            )}

            {/* Primary CTA — config-driven label (manual: Approve & Download, automated: Send Connection Requests) */}
            {sendCount > 0 && !progress && (
                <div>
                    <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                        <AlertDialogTrigger asChild>
                            <Button
                                className="w-full min-h-[48px] gap-2 bg-brand-900 hover:bg-brand-dark text-white text-base font-semibold shadow-sm"
                                disabled={sending}
                            >
                                {manualWorkflowEnabled
                                    ? <DownloadSimple weight="bold" className="w-5 h-5" />
                                    : <PaperPlaneTilt weight="bold" className="w-5 h-5" />
                                }
                                {workflowLabels.primary_button_label} ({sendCount})
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle className="text-lg font-bold">
                                    {(workflowLabels.confirm_title || '')
                                        .replace('{count}', sendCount)
                                        .replace('{noun}', sendCount === 1 ? 'Candidate' : 'Candidates')}
                                </AlertDialogTitle>
                                <AlertDialogDescription className="text-base text-slate-600">
                                    {(workflowLabels.confirm_description || '')
                                        .replace(/{count}/g, sendCount)
                                        .replace(/{noun}/g, sendCount === 1 ? 'candidate' : 'candidates')}
                                    {checkedIds.size > 0 && (
                                        <span className="block mt-2 text-slate-500">
                                            {checkedIds.size} checked {checkedIds.size === 1 ? 'candidate is' : 'candidates are'} excluded from this batch.
                                        </span>
                                    )}
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel className="min-h-[44px] text-base">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                    className="min-h-[44px] text-base bg-brand-900 hover:bg-brand-dark"
                                    onClick={handleExecute}
                                >
                                    {workflowLabels.confirm_button}
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            )}
        </div>
    );
}
